import {
  ArrowPathRoundedSquareIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import React from "react";
import Tooltip from "../common/Tooltip";

/**
 * The Cmd/Ctrl+F find panel shared by the narrative editor and the
 * transcription tab. Purely presentational: the owner runs the search and
 * passes back the counts. Stays mounted and slides open/closed on `open`;
 * the input mounts with it so it autofocuses on every open. Replace is opt-in
 * (`onToggleReplace` + extra rows as `children`), so read-only surfaces like
 * the transcript get find without it.
 */
interface FindBarProps {
  open: boolean;
  /** Extra rows under the find row (redaction chips, replace input) */
  children?: React.ReactNode;
  /** Room for those rows while open; the default fits the find row alone */
  tall?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  searchTerm: string;
  onSearch: (term: string) => void;
  current: number;
  total: number;
  onNext: () => void;
  onPrevious: () => void;
  caseSensitive: boolean;
  onToggleCaseSensitive: () => void;
  onClose: () => void;
  replaceOpen?: boolean;
  onToggleReplace?: () => void;
}

const FindBar = ({
  open,
  children,
  tall = false,
  inputRef,
  searchTerm,
  onSearch,
  current,
  total,
  onNext,
  onPrevious,
  caseSensitive,
  onToggleCaseSensitive,
  onClose,
  replaceOpen = false,
  onToggleReplace,
}: FindBarProps) => (
  <div
    data-tour="find-bar"
    className={cn(
      "overflow-hidden bg-base-100 transition-all duration-200 ease-in-out",
      open
        ? cn(tall ? "max-h-60" : "max-h-40", "opacity-100 p-2")
        : "max-h-0 opacity-0 p-0",
    )}
  >
    {open && (
      <div className="flex flex-col">
        <div className="flex items-center space-x-1">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.shiftKey) {
                    onPrevious();
                  } else {
                    onNext();
                  }
                }
              }}
              placeholder="Find"
              className="input input-bordered max-h-7 w-full focus:outline-none focus:border-blue-500 border-grey-500 dark:border-base-300 transition-colors disabled:bg-grey-50 dark:disabled:bg-base-200 disabled:border-grey-500 dark:disabled:border-base-300 disabled:text-gray-600 dark:disabled:text-base-content/70 bg-base-100 text-sm px-2 pr-14"
              autoFocus
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-base-content/40 pointer-events-none select-none">
              {total > 0 ? `${current}/${total}` : "0/0"}
            </span>
          </div>
          <Tooltip overlay="Previous (Shift+Enter)">
            <button
              onClick={onPrevious}
              disabled={total === 0}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronUpIcon className="size-4" />
            </button>
          </Tooltip>
          <Tooltip overlay="Next (Enter)">
            <button
              onClick={onNext}
              disabled={total === 0}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronDownIcon className="size-4" />
            </button>
          </Tooltip>
          <Tooltip overlay="Match Case">
            <button
              onClick={onToggleCaseSensitive}
              className={cn(
                "p-1.5 rounded transition-colors font-semibold text-xs",
                caseSensitive
                  ? "bg-blue-100 hover:bg-blue-200 text-blue-700"
                  : "hover:bg-gray-100 dark:hover:bg-base-200 text-gray-600 dark:text-base-content/70",
              )}
            >
              Aa
            </button>
          </Tooltip>
          {onToggleReplace && (
            <Tooltip overlay="Replace" placement="bottomRight">
              <button
                data-tour="find-replace-toggle"
                onClick={onToggleReplace}
                className={cn(
                  "p-1.5 rounded transition-colors",
                  replaceOpen
                    ? "bg-blue-100 hover:bg-blue-200"
                    : "hover:bg-gray-100 dark:hover:bg-base-200",
                )}
              >
                <ArrowPathRoundedSquareIcon className="size-4" />
              </button>
            </Tooltip>
          )}
          <Tooltip overlay="Close (Esc)" placement="bottomRight">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded"
            >
              <XMarkIcon className="size-4" />
            </button>
          </Tooltip>
        </div>
        {children}
      </div>
    )}
  </div>
);

export default FindBar;
